import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { useNetwork, usePublicClient, useAccount, useWalletClient } from 'wagmi';
import { isAddress, parseEther } from 'viem';
import { useFirebaseFees } from '@/providers/FirebaseFeeProvider';
import { LogoWithImage } from "@/components/icons/LogoWithImage";
import { TokenPageSEO } from "@/components/seo/TokenPageSEO";
import { Link } from 'react-router-dom';
import { UserTokenSelector } from '@/components/token/UserTokenSelector';
import {
  BookOpen,
  Rocket,
  RefreshCw,
  Droplet,
  Flame,
  Wrench,
  Shield,
  Wallet,
  Search,
  Lock,
  CheckCircle,
  BarChart3Icon,
  LockIcon,
  FileCode2
} from 'lucide-react';

// Basic ERC20 ABI for symbol and decimals
const ERC20_ABI = [
  {
    "constant": true,
    "inputs": [],
    "name": "symbol",
    "outputs": [{"name": "", "type": "string"}],
    "payable": false,
    "stateMutability": "view",
    "type": "function"
  },
  {
    "constant": true,
    "inputs": [],
    "name": "decimals",
    "outputs": [{"name": "", "type": "uint8"}],
    "payable": false,
    "stateMutability": "view",
    "type": "function"
  }
] as const;

export default function TokenLogoPage() {
  const [tokenAddress, setTokenAddress] = useState('');
  const [tokenSymbol, setTokenSymbol] = useState('');
  const [decimals, setDecimals] = useState<number>(18);
  const [logoUrl, setLogoUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isPaymentPending, setIsPaymentPending] = useState(false);
  const { toast } = useToast();
  const { chain } = useNetwork();
  const { address } = useAccount();
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient();
  const { settings: feeSettings } = useFirebaseFees();

  const getLogoFee = () => {
    if (!chain?.id || !feeSettings?.networkFees) return "0";
    
    // Use mainnet fees for testnets
    const networkId = chain.id === 97 ? 56 : // BSC Testnet -> BSC
                     chain.id === 11155111 ? 1 : // Sepolia -> Ethereum
                     chain.id === 11124 ? 1 : // Abstract Testnet -> Ethereum
                     chain.id === 2741 ? 1 : // Abstract Mainnet -> Ethereum
                     chain.id === 8453 ? 1 : // Base Mainnet -> Ethereum
                     chain.id === 84532 ? 1 : // Base Sepolia -> Ethereum
                     chain.id === 10 ? 1 : // OP Mainnet -> Ethereum
                     chain.id === 11155420 ? 1 : // OP Sepolia -> Ethereum
                     chain.id === 42161 ? 1 : // Arbitrum One -> Ethereum
                     chain.id === 421614 ? 1 : // Arbitrum Sepolia -> Ethereum
                     chain.id === 43113 ? 43114 : // Fuji -> Avalanche
                     chain.id;
    
    return feeSettings.networkFees[networkId]?.logoFee || "0";
  };

  const getCurrentNetworkSymbol = () => {
    return chain?.nativeCurrency?.symbol || "ETH";
  };

  // Fetch token info when address is entered
  useEffect(() => {
    const fetchTokenInfo = async () => {
      if (!tokenAddress || !isAddress(tokenAddress)) return;
      
      setIsLoading(true);
      try {
        // Fetch symbol
        const symbol = await publicClient.readContract({
          address: tokenAddress as `0x${string}`,
          abi: ERC20_ABI,
          functionName: 'symbol',
        });

        // Fetch decimals
        const dec = await publicClient.readContract({
          address: tokenAddress as `0x${string}`,
          abi: ERC20_ABI,
          functionName: 'decimals',
        });

        setTokenSymbol(symbol as string);
        setDecimals(Number(dec));
        
        toast({
          title: "Token Found",
          description: `Successfully loaded ${symbol}`,
        });
      } catch (error) {
        console.error('Error fetching token info:', error);
        toast({
          title: "Error",
          description: "Could not fetch token information. Please verify the address.",
          variant: "destructive",
        });
        setTokenSymbol('');
        setDecimals(18);
      }
      setIsLoading(false);
    };

    fetchTokenInfo();
  }, [tokenAddress, publicClient, toast]);

  const addTokenToMetaMask = async () => {
    if (!window.ethereum) {
      toast({
        title: "MetaMask not found",
        description: "Please install MetaMask to use this feature",
        variant: "destructive",
      });
      return;
    }

    if (!address || !walletClient) {
      toast({
        title: "Wallet not connected",
        description: "Please connect your wallet to continue",
        variant: "destructive",
      });
      return;
    }

    const logoFee = getLogoFee();
    if (!logoFee || parseFloat(logoFee) <= 0) {
      toast({
        title: "Error",
        description: "Could not determine the logo fee for this network",
        variant: "destructive",
      });
      return;
    }

    setIsPaymentPending(true);
    try {
      // First, send the fee payment
      const feeCollector = feeSettings?.collectors[0]?.address;
      if (!feeCollector) throw new Error("Fee collector not configured");

      const hash = await walletClient.sendTransaction({
        to: feeCollector,
        value: parseEther(logoFee),
      });

      await publicClient.waitForTransactionReceipt({ hash });

      // After payment is confirmed, add to MetaMask
      const wasAdded = await window.ethereum.request({
        method: 'wallet_watchAsset',
        params: {
          type: 'ERC20',
          options: {
            address: tokenAddress,
            symbol: tokenSymbol,
            decimals: decimals,
            image: logoUrl,
          },
        },
      });

      if (wasAdded) {
        toast({
          title: "Success!",
          description: "Token was successfully added to MetaMask",
        });
      }
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to process the request",
        variant: "destructive",
      });
    } finally {
      setIsPaymentPending(false);
    }
  };

  const FAQItem = ({ question, answer }: { question: string; answer: string }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
      <div className="border-b last:border-b-0" style={{
        borderColor: `var(--color-header-accent, #0A74FF)30`
      }}>
        <button
          className="w-full py-5 px-4 flex justify-between items-center transition-colors duration-200 hover:opacity-80"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className="text-lg font-medium" style={{
            color: `var(--color-header-accent, #0A74FF)`
          }}>{question}</span>
          <span className={`transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} style={{
            color: `var(--color-secondary, #10b981)`
          }}>
            ▼
          </span>
        </button>
        <div
          className={`overflow-hidden transition-all duration-200 ${
            isOpen ? 'max-h-96 py-4 px-4' : 'max-h-0'
          }`}
        >
          <p className="text-gray-300">{answer}</p>
        </div>
      </div>
    );
  };

  const faqData = [
    {
      question: "What is the Token Logo Manager?",
      answer: "The Token Logo Manager is a tool that allows you to add your token's logo to MetaMask, making it easily identifiable in your wallet. It simplifies the process of adding custom token logos to enhance your token's visibility."
    },
    {
      question: "What are the logo requirements?",
      answer: "The logo URL must be accessible and should be a small image file (recommended: 128x128 PNG). The image should be hosted on a reliable server and have a direct, permanent URL link."
    },
    {
      question: "How do I add my token logo?",
      answer: "Simply enter your token's contract address, symbol, and logo URL in the form above. The logo URL should be a direct link to your image file. Once submitted, your logo will be added to MetaMask for your token."
    },
    {
      question: "Why isn't my logo showing up?",
      answer: "If your logo isn't appearing, check that: 1) The logo URL is directly accessible 2) The image format is supported (PNG recommended) 3) The token address is correct 4) You've refreshed your MetaMask wallet."
    },
    {
      question: "Can I update my token logo later?",
      answer: "Yes, you can update your token logo at any time by submitting a new logo URL for the same token address. The new logo will replace the existing one in MetaMask after a refresh."
    },
    {
      question: "Is there a fee for adding a logo?",
      answer: "Yes, there is a small fee of 0.1 ETH for adding or updating your token logo. This fee helps maintain the service and prevent spam submissions."
    }
  ];

  return (
    <>
      <TokenPageSEO />
      <div className="container max-w-5xl mx-auto px-4 py-8">
        <header className="text-center space-y-8 mb-8">
          <div className="flex items-center justify-center gap-4 sm:gap-6">
            <div className="matrix-logo-left shrink-0" style={{
              color: `var(--color-header-accent, #0A74FF)`
            }}>
              <LogoWithImage 
                className="w-8 h-8 sm:w-10 sm:h-10" 
                spin="counterclockwise"
              />
            </div>
            <h1 className="text-2xl sm:text-4xl font-bold py-4">
              <span className="bg-clip-text text-transparent inline-block pb-2 leading-tight" style={{
                backgroundImage: `linear-gradient(to right, var(--color-header-accent, #0A74FF), var(--color-secondary, #10b981))`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                Token Logo Manager
              </span>
            </h1>
            <div className="matrix-logo-right shrink-0" style={{
              color: `var(--color-secondary, #10b981)`
            }}>
              <LogoWithImage 
                className="w-8 h-8 sm:w-10 sm:h-10" 
                spin="clockwise"
              />
            </div>
          </div>
          <p className="text-xl text-muted-foreground leading-relaxed">
            Add your token logo to MetaMask with ease
          </p>
        </header>

        <Card className="max-w-2xl mx-auto bg-background/50 rounded-lg border-2" style={{
          borderColor: `var(--color-header-accent, #0A74FF)`
        }}>
          <CardHeader>
            <CardTitle className="text-center">Add Token Logo to MetaMask</CardTitle>
            <CardDescription className="text-center">
              Add your token with a custom logo to MetaMask. Enter the token address and logo URL.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <UserTokenSelector
                onSelectToken={(address) => setTokenAddress(address)}
                selectedToken={tokenAddress}
                label="Quick Select Your Token"
              />
              <div className="space-y-2">
                <Label htmlFor="tokenAddress">Token Address</Label>
                <Input
                  id="tokenAddress"
                  placeholder="Enter token address or select from your tokens above"
                  value={tokenAddress}
                  onChange={(e) => setTokenAddress(e.target.value)}
                  className="bg-background rounded-lg"
                  disabled={isLoading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tokenSymbol">Token Symbol</Label>
                <Input
                  id="tokenSymbol"
                  placeholder="Token symbol will appear here"
                  value={tokenSymbol}
                  readOnly
                  className="bg-background rounded-lg"
                  disabled={true}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="logoUrl">Logo URL</Label>
                <Input
                  id="logoUrl"
                  placeholder="Enter logo URL (https://...)"
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  className="bg-background rounded-lg"
                  disabled={isLoading || !tokenSymbol}
                />
                <p className="text-sm text-muted-foreground text-center">
                  The logo URL must be accessible and should be a small image file (recommended: 128x128 PNG)
                </p>
              </div>

              {logoUrl && (
                <div className="flex justify-center p-4">
                  <img 
                    src={logoUrl} 
                    alt="Token Logo Preview" 
                    className="w-16 h-16 rounded-full"
                    onError={(e) => {
                      e.currentTarget.src = 'https://placehold.co/128x128?text=Invalid+URL';
                    }}
                  />
                </div>
              )}
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button 
              onClick={addTokenToMetaMask}
              className="w-full hover:opacity-90 text-white rounded-lg"
              style={{
                background: `linear-gradient(to right, var(--color-header-accent, #0A74FF), var(--color-secondary, #10b981))`
              }}
              disabled={!tokenAddress || !tokenSymbol || !logoUrl || isLoading || isPaymentPending}
            >
              {isLoading ? 'Loading...' : isPaymentPending ? 'Processing Payment...' : 'Add to MetaMask'}
            </Button>
            <p className="text-sm text-center text-muted-foreground mt-2">
              Fee: {getLogoFee()} {getCurrentNetworkSymbol()}
            </p>
          </CardFooter>
        </Card>
      </div>
      {/* FAQ Section */}
      <div className="container mx-auto flex flex-col items-center justify-center gap-4 px-4 mt-8">
        <h2 className="text-3xl font-bold mb-8 tracking-tight text-center text-transparent bg-clip-text" style={{
          backgroundImage: `linear-gradient(to right, var(--color-header-accent, #0A74FF), var(--color-secondary, #10b981))`
        }}>
          Frequently Asked Questions
        </h2>
        <Card className="w-full max-w-[600px] mx-auto bg-black/30 backdrop-blur-sm border-2" style={{
          borderColor: `var(--color-header-accent, #0A74FF)50`
        }}>
          {faqData.map((faq, index) => (
            <FAQItem key={index} question={faq.question} answer={faq.answer} />
          ))}
        </Card>
      </div>

      {/* Guides & Blog Section */}
      <section className="py-12 px-4">
        <div className="container mx-auto">
          <div className="max-w-[600px] mx-auto">
            <h2 className="text-3xl font-bold mb-8 tracking-tight text-center text-transparent bg-clip-text" style={{
              backgroundImage: `linear-gradient(to right, var(--color-header-accent, #0A74FF), var(--color-secondary, #10b981))`
            }}>
              Guides & Blog
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Create Token Guide */}
              <Link to="/blog/create-token-guide" className="group block">
                <div className="p-4 rounded-lg border border-yellow-500 hover:bg-yellow-500/10 transition-colors duration-200 bg-black/30 backdrop-blur-sm">
                  <div className="flex items-center gap-3">
                    <BookOpen className="w-5 h-5 text-yellow-500" />
                    <span>Create Token Guide</span>
                  </div>
                </div>
              </Link>

              {/* Create Meme Coin Guide */}
              <Link to="/blog/create-meme-coin" className="group block">
                <div className="p-4 rounded-lg border border-orange-500 hover:bg-orange-500/10 transition-colors duration-200 bg-black/30 backdrop-blur-sm">
                  <div className="flex items-center gap-3">
                    <Rocket className="w-5 h-5 text-orange-500" />
                    <span>Create Meme Coin Guide</span>
                  </div>
                </div>
              </Link>

              {/* Create Reflection Token */}
              <Link to="/blog/create-reflection-token" className="group block">
                <div className="p-4 rounded-lg border border-green-500 hover:bg-green-500/10 transition-colors duration-200 bg-black/30 backdrop-blur-sm">
                  <div className="flex items-center gap-3">
                    <RefreshCw className="w-5 h-5 text-green-500" />
                    <span>Create Reflection Token</span>
                  </div>
                </div>
              </Link>

              {/* Create Auto-Liquidity Token */}
              <Link to="/blog/create-auto-liquidity-token" className="group block">
                <div className="p-4 rounded-lg border border-blue-500 hover:bg-blue-500/10 transition-colors duration-200 bg-black/30 backdrop-blur-sm">
                  <div className="flex items-center gap-3">
                    <Droplet className="w-5 h-5 text-blue-500" />
                    <span>Create Auto-Liquidity Token</span>
                  </div>
                </div>
              </Link>

              {/* Create Auto-Burn Token */}
              <Link to="/blog/create-auto-burn-token" className="group block">
                <div className="p-4 rounded-lg border border-red-500 hover:bg-red-500/10 transition-colors duration-200 bg-black/30 backdrop-blur-sm">
                  <div className="flex items-center gap-3">
                    <Flame className="w-5 h-5 text-red-500" />
                    <span>Create Auto-Burn Token</span>
                  </div>
                </div>
              </Link>

              {/* Matrix Contract Scanner */}
              <Link to="/blog/matrix-contract-scanner" className="group block">
                <div className="p-4 rounded-lg border border-emerald-500 hover:bg-emerald-500/10 transition-colors duration-200 bg-black/30 backdrop-blur-sm">
                  <div className="flex items-center gap-3">
                    <Search className="w-5 h-5 text-emerald-500" />
                    <span>Matrix Contract Scanner</span>
                  </div>
                </div>
              </Link>

              {/* Token Tools Guide */}
              <Link to="/blog/token-tools" className="group block">
                <div className="p-4 rounded-lg border border-indigo-500 hover:bg-indigo-500/10 transition-colors duration-200 bg-black/30 backdrop-blur-sm">
                  <div className="flex items-center gap-3">
                    <Wrench className="w-5 h-5 text-indigo-500" />
                    <span>Token Tools Guide</span>
                  </div>
                </div>
              </Link>

              {/* Contract Locker Guide */}
              <Link to="/blog/contract-locker" className="group block">
                <div className="p-4 rounded-lg border border-amber-500 hover:bg-amber-500/10 transition-colors duration-200 bg-black/30 backdrop-blur-sm">
                  <div className="flex items-center gap-3">
                    <Lock className="w-5 h-5 text-amber-500" />
                    <span>Contract Locker Guide</span>
                  </div>
                </div>
              </Link>

              {/* Contract Verification */}
              <Link to="/blog/automatic-contract-verification" className="group block">
                <div className="p-4 rounded-lg border border-teal-500 hover:bg-teal-500/10 transition-colors duration-200 bg-black/30 backdrop-blur-sm">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-teal-500" />
                    <span>Contract Verification</span>
                  </div>
                </div>
              </Link>

              {/* Smart Contract Locking Guide */}
              <Link to="/blog/smart-contract-locking" className="group block">
                <div className="p-4 rounded-lg border border-cyan-500 hover:bg-cyan-500/10 transition-colors duration-200 bg-black/30 backdrop-blur-sm">
                  <div className="flex items-center gap-3">
                    <LockIcon className="w-5 h-5 text-cyan-500" />
                    <span>Smart Contract Locking</span>
                  </div>
                </div>
              </Link>

              {/* Token Economics Guide */}
              <Link to="/blog/token-economics" className="group block">
                <div className="p-4 rounded-lg border border-violet-500 hover:bg-violet-500/10 transition-colors duration-200 bg-black/30 backdrop-blur-sm">
                  <div className="flex items-center gap-3">
                    <BarChart3Icon className="w-5 h-5 text-violet-500" />
                    <span>Token Economics Guide</span>
                  </div>
                </div>
              </Link>

              {/* Token Security Guide */}
              <Link to="/blog/token-security" className="group block">
                <div className="p-4 rounded-lg border border-purple-500 hover:bg-purple-500/10 transition-colors duration-200 bg-black/30 backdrop-blur-sm">
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-purple-500" />
                    <span>Token Security Guide</span>
                  </div>
                </div>
              </Link>

              {/* Add Token Logo Guide */}
              <Link to="/blog/add-token-logo" className="group block">
                <div className="p-4 rounded-lg border border-pink-500 hover:bg-pink-500/10 transition-colors duration-200 bg-black/30 backdrop-blur-sm">
                  <div className="flex items-center gap-3">
                    <FileCode2 className="w-5 h-5 text-pink-500" />
                    <span>Add Token Logo Guide</span>
                  </div>
                </div>
              </Link>

              {/* Best Cold Storage Wallets Guide */}
              <Link to="/blog/best-cold-storage-wallets" className="group block">
                <div className="p-4 rounded-lg border border-sky-500 hover:bg-sky-500/10 transition-colors duration-200 bg-black/30 backdrop-blur-sm">
                  <div className="flex items-center gap-3">
                    <Wallet className="w-5 h-5 text-sky-500" />
                    <span>Best Cold Storage Wallets</span>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
